import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Layout } from './Layout'
import { t } from '../i18n/i18n'
import { Button, makeStyles, MessageBar, MessageBarBody, MessageBarTitle, tokens } from '@fluentui/react-components'
import { CalendarAdd24Regular } from '@fluentui/react-icons'
import dayjs from 'dayjs'
import { CalendarChart } from '../components/calendar/CalendarChart'
import { ModalActivity } from '../components/calendar/ModalActivity'
import { Activity } from '../api/schemas/schema'
import { DataContext } from '../context/DataContext'
import { useSearchParams } from 'react-router-dom'
import { filterActivitiesByStatus, getActivities, parseActivityStatusParam } from '../helpers/calendarHelper'
import { ActivityStatusFilter, STATUS_PARAM } from '../components/calendar/ActivityStatusFilter'
import { Loader } from '../components/common/spinner/Loader'
import { PhoneList } from '../components/common/list/PhoneList'
import { Text } from '../components/common/text/Text'
import { formatDate } from '../helpers/stringHelper'
import { NavigationLink } from '../interfaces/INavigationLink'

const useStyles = makeStyles({
   messageBar: {
      width: '100%',
   },
   // The filter lives in Layout's sticky header next to the button, so it stays reachable while the
   // wheel scrolls instead of scrolling away with the page.
   headerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacingHorizontalL,
      '@media screen and (max-width: 640px)': {
         width: '100%',
         flexDirection: 'column',
         alignItems: 'stretch',
      },
   },
   headerFilter: {
      '@media screen and (max-width: 1007px)': {
         width: 'auto',
      },
      '@media screen and (max-width: 640px)': {
         width: '100%',
      },
   },
   activityContainer: {
      borderRadius: '5px',
      boxShadow: tokens.shadow8,
      padding: '5px',
      paddingLeft: '19px',
      paddingRight: '19px',
   },
   calendarView: {
      display: 'none',
      '@media screen and (min-width: 641px)': {
         display: 'flex',
         flexDirection: 'column',
         gap: tokens.spacingVerticalXXL,
      },
   },
   chartWrapper: {
      position: 'relative',
   },
   phoneView: {
      display: 'none',
      '@media screen and (max-width: 640px)': {
         display: 'flex',
         flexDirection: 'column',
         gap: tokens.spacingVerticalXXL,
      },
   },
   phoneBtn: {
      display: 'none',
      '@media screen and (max-width: 640px)': {
         width: `calc(100% - (${tokens.spacingHorizontalL} * 2))`,
         display: 'inline-flex',
         position: 'fixed',
         bottom: '60px',
      },
   },
   displayPhoneView: {
      '@media screen and (min-width: 641px)': {
         display: 'none',
      },
   },
   addBtn: {
      height: '44px',
   },
})

export const Calendar = () => {
   const dataContext = useContext(DataContext)
   const classes = useStyles()
   const currentYear = dayjs().year()
   const [loading, setIsLoading] = useState(true)
   const [showLoader, setShowLoader] = useState(false)
   const [year, setYear] = useState({ name: currentYear, value: 1 })
   const [activities, setActivities] = useState<Activity[]>([])
   const [modalState, setModalState] = useState<{ isOpen: boolean; selectedActivity?: Activity }>({
      isOpen: false,
      selectedActivity: undefined,
   })
   const [searchParams] = useSearchParams()
   const status = parseActivityStatusParam(searchParams.get(STATUS_PARAM))

   // Derived, never state: setActivities is handed to ModalActivity, which rewrites the list on save
   // and delete, so the fetched activities have to stay the single source.
   const visibleActivities = useMemo(() => filterActivitiesByStatus(activities, status), [activities, status])
   const hiddenByFilter = !loading && activities.length > 0 && visibleActivities.length === 0
   useEffect(() => {
      if (!dataContext.state.organisation?.id) return
      const ac = new AbortController()
      setActivities([]) // avoid showing the previous year's activities on the new year's wheel while this fetch is in flight
      getActivities(dataContext, year.name, setActivities, setIsLoading, ac.signal)
      return () => ac.abort()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [year, dataContext.state.organisation?.id])

   useEffect(() => {
      if (!loading) {
         setShowLoader(false)
         return
      }
      // Only show the loading overlay if the fetch is actually slow — otherwise fast fetches just blink the overlay on/off.
      const timeout = setTimeout(() => setShowLoader(true), 150)
      return () => clearTimeout(timeout)
   }, [loading])

   const onActivitySelect = (activity: Activity) => {
      setModalState({ isOpen: true, selectedActivity: activity })
   }

   // Clears activities and flags loading in the same event handler as the year change (not in the effect
   // below, which only runs after that render already committed) so the wheel never briefly renders the new
   // year's date math against the previous year's activities, and the "no activities" message never flashes
   // in the one-render gap between activities being cleared and the real fetch setting loading itself.
   const changeYear = (newYear: { name: number; value: number }) => {
      setActivities([])
      setIsLoading(true)
      setYear(newYear)
   }

   return (
      <Layout
         breadCrumbItems={[{ title: t('AnnualCalendar'), active: true, path: NavigationLink.AnnualCalendar }]}
         activeDesktopLink={NavigationLink.AnnualCalendar}
         button={
            <div className={classes.headerActions}>
               <ActivityStatusFilter className={classes.headerFilter} />
               <Button appearance="primary" onClick={() => setModalState({ isOpen: true })} icon={<CalendarAdd24Regular />} className={classes.addBtn}>
                  {t('PlanActivity')}
               </Button>
            </div>
         }
         overflowAuto={true}
      >
         <MessageBar className={classes.messageBar} style={{ visibility: !loading && (activities.length < 1 || hiddenByFilter) ? 'visible' : 'hidden' }}>
            <MessageBarBody>
               {hiddenByFilter ? (
                  <MessageBarTitle>{t('NoActivitiesWithStatus')}</MessageBarTitle>
               ) : (
                  <>
                     <div className={classes.calendarView}>
                        <MessageBarTitle>{t('PlanActivitesMsg')}</MessageBarTitle>
                     </div>
                     <div className={classes.phoneView}>
                        <MessageBarTitle>{t('PlanActivitesPhoneViewMsg')}</MessageBarTitle>
                     </div>
                  </>
               )}
            </MessageBarBody>
         </MessageBar>
         <div className={classes.calendarView}>
            <div className={classes.chartWrapper}>
               {showLoader && <Loader opacity />}
               <CalendarChart activities={visibleActivities} year={year} setYear={changeYear} onActivitySelect={onActivitySelect} />
            </div>
            <ModalActivity modalState={modalState} setActivities={setActivities} setModalState={setModalState} currentYear={year.name} />
         </div>
         <div className={classes.displayPhoneView}>
            {showLoader ? (
               <Loader />
            ) : (
               <>
                  <PhoneList
                     items={visibleActivities}
                     onRowClick={(activity) => onActivitySelect(activity)}
                     renderBodyItem={(item: Activity) => (
                        <div className={classes.activityContainer}>
                           <span>
                              <Text weight="bold">{item.title}</Text>
                           </span>
                           <Text>{`${formatDate(item.startDate)} ${t('To')} ${formatDate(item.endDate)}`}</Text>
                           <Text>{item?.description}</Text>
                        </div>
                     )}
                  />
               </>
            )}
         </div>
      </Layout>
   )
}
